import type { IOrder, ISamplePool } from 'Model/index.js'
import { useEffect, useRef, useState } from 'react'
import { getIconFromTaxa } from 'Util/iconUtil.js'
import type { Epic } from 'Flux/index.js'
import { addEpic, useDispatch } from 'Flux/index.js'
import { toggleCompactMode } from 'Flux/Slice/Application/ApplicationSlice.js'
import { orderSlice } from 'Flux/Slice/Order/OrderSlice.js'
import { Toast } from 'View/Common/index.js'
import { map, Subject } from 'rxjs'
import {
  useGetAuthenticatedUser,
  useGetCompactMode,
  useGetIntermediatesStatusSelector,
  useGetOtuProcessingDataSelector,
  useOrderNotices,
  useGetPipelineStateSelector
} from '../../../Flux/Selector/index.js'
import type { OrderState } from '../../../Util/OrderState.js'
import { determineOrderState } from '../../../Util/OrderState.js'
import { ofType } from 'redux-observable'
import { useGetSampleMetaDataSelector } from 'Flux/Selector/Order/GetSampleMetaDatas/GetSampleMetaDataSelector.js'
import { orderToStateWidget } from './OrderStateWidget.js'
import { OrderContextActions } from './Actions/OrderContextActionsWidget.tsx'
import { TooltipTrigger } from '../../../Provider/TooltipManager.js'
import SamplesMap from 'View/Common/SamplesMap.tsx'
import { useLocation, useNavigate } from 'react-router-dom'
import { OrderDetailInfoWidget } from './OrderDetail/OrderDetailInfoWidget.js'
import { OrderDetailActionBar } from './OrderDetail/OrderDetailActionBar.js'
import { OrderDetailTabSection } from './OrderDetail/OrderDetailTabSection.js'
import { useModal } from '../../Modal/UseModal.js'
import { trackOrderDwellTime, trackOrderPageView } from '../../../Services/Telemetry/index.js'
import { getDetectedSequencingPlatform, getPlatformProcessingTypeConflict } from '../../../Util/sequencingPlatform.js'
import { awaitsPostprocessing } from '../../../Util/IntermediatesLifetime.ts'
import { IntermediatesBanner, IntermediatesExpiredBadge } from './OrderDetail/IntermediatesNotice.tsx'
import { canUseSpinOnOrder } from '../../../Model/User/featureGating.ts'
import { describeSpinStatus } from '../../../Model/Spin/spinStatus.ts'
import { SpinPendingBadge, useSpinScopeStatus } from '../../Component/SpinStatusLabel.tsx'
import { OrderAlertIndicator } from '../../Component/OrderAlert/OrderAlertIndicator.tsx'
import { unassignedAlerts } from '../../../Model/Order/unassignedReads.ts'
import { noticeAlerts } from '../../../Model/Order/orderNotices.ts'
import { qualityFilterLossAlerts } from '../../../Model/Order/qualityFilterLoss.ts'

interface OrderDetailProps {
  order: IOrder
  samplePools: ISamplePool[] | 'LOADING'
  tokenChecksum?: string
  otuCount?: number
}

export const OrderDetail = (props: OrderDetailProps) => {
  const message$ = useRef(new Subject<{ type: 'success' | 'error' | 'warning' | 'info'; message: string }>()).current
  const user = useGetAuthenticatedUser()
  const [orderState, setOrderState] = useState<OrderState>('UNKNOWN')
  const pipelineState = useGetPipelineStateSelector()
  const intermediates = useGetIntermediatesStatusSelector()
  const otuProcessingData = useGetOtuProcessingDataSelector()
  const notices = useOrderNotices(props.order.id)
  const metaData = useGetSampleMetaDataSelector()
  const isCompactMode = useGetCompactMode()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { openModal } = useModal()
  const spinStatus = describeSpinStatus(
    useSpinScopeStatus(props.order.id, canUseSpinOnOrder(props.order, user) && !!props.order.orderDone),
    'ORDER'
  )

  useEffect(() => {
    setOrderState(determineOrderState(props.order, props.samplePools))
  }, [props.order, props.samplePools])

  const poolItems = props.samplePools === 'LOADING' ? [] : props.samplePools.flatMap((sp) => sp.samples)

  useEffect(() => {
    const OrderSubmitSuccessfullEpic: Epic = (action$, state$, { [orderSlice.name]: order }) => {
      return action$.pipe(
        ofType(order.submitOrderSuccess.type),
        map(() => {
          message$.next({ type: 'success', message: `Order submitted successfully!` })
          return { type: 'ok' }
        })
      )
    }

    const destructor = addEpic(OrderSubmitSuccessfullEpic)
    return () => destructor()
  }, [])

  // ---------------------------------------------------------------------------
  // Engagement tracking: page view + dwell time
  // ---------------------------------------------------------------------------

  // Revisit detection via sessionStorage (survives SPA navigation, not full reloads).
  // Full-reload revisits are classified as 'initial' — the common case.
  const viewKey = `order_${props.order.id}_viewed`
  const wasViewed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(viewKey) === '1'

  useEffect(() => {
    trackOrderPageView(props.order, wasViewed ? 'reopen' : 'initial')
    if (!wasViewed && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(viewKey, '1')
    }
  }, [props.order.id]) // only re-fire if the order ID changes (not on props.order reference change)

  // Dwell time: visible time between mount and leave, emitted exactly once.
  // We still do NOT *emit* on `visibilitychange` — that would latch a few seconds if the user
  // briefly tabs away and then keeps working. Instead we pause the clock while the tab is hidden
  // and resume it when it comes back, then emit on SPA unmount (effect cleanup) OR `pagehide`
  // (tab close / reload / bfcache — more reliable than `beforeunload`), whichever comes first.
  // Measuring wall-clock instead meant a tab parked overnight reported as a 17-hour "dwell".
  useEffect(() => {
    let visibleSince: number | null = document.visibilityState === 'visible' ? Date.now() : null
    let accumulatedMs = 0
    let emitted = false

    const settle = () => {
      if (visibleSince !== null) {
        accumulatedMs += Date.now() - visibleSince
        visibleSince = null
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (visibleSince === null) visibleSince = Date.now()
      } else {
        settle()
      }
    }

    const emitDwell = () => {
      if (emitted) return
      emitted = true
      settle()
      trackOrderDwellTime(Math.round(accumulatedMs / 1000), props.order)
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', emitDwell)

    return () => {
      emitDwell() // SPA navigation away / order switch
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', emitDwell)
    }
  }, [props.order.id])

  const platformConflict = getPlatformProcessingTypeConflict({
    detectedSequencingPlatform: getDetectedSequencingPlatform(props.order, props.samplePools),
    sequencingPlatformOverride: props.order.sequencingPlatformOverride,
    processingType: props.order.processingType
  })

  return (
    <div className="overflow-x-hidden w-full max-w-screen-2xl mx-auto px-4" id="orderDetail">
      {!user && (
        <div className={'flex justify-center p-4 mb-4 border border-rounded shadow-sm bg-primary underline'}>
          <div onClick={() => navigate(`/register?redirect=${encodeURIComponent(location.pathname + location.search)}`)}>
            Don&#39;t have an account? Click here to register!
          </div>
        </div>
      )}
      <div className={'flex items-stretch text-sm md:flex-row sm:flex-col sm:gap-2 pb-2'}>
        <div className="flex-1 flex flex-wrap items-center gap-4">
          <TooltipTrigger
            tooltip={
              'This is your unique spcfy order id. Please include it in requests to the support. Click to copy the id to your clipboard'
            }
          >
            <span
              data-testid="order-id"
              className={'text-neutral-400 align-baseline'}
              onClick={() => navigator.clipboard.writeText(props.order.id)}
            >
              #{props.order.id}
            </span>
          </TooltipTrigger>
          <TooltipTrigger tooltip="Hide empty details and actions you cannot use on this order.">
            <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap text-neutral-600">
              <input
                type="checkbox"
                data-testid="compact-mode-toggle"
                className="toggle toggle-sm"
                checked={isCompactMode}
                onChange={() => dispatch(toggleCompactMode())}
              />
              Compact view
            </label>
          </TooltipTrigger>
        </div>
        <OrderContextActions
          samplePools={props.samplePools === 'LOADING' ? [] : props.samplePools}
          order={props.order}
          tokenChecksum={props.tokenChecksum}
          orderState={orderState}
          hideDisabled={isCompactMode}
        />
      </div>

      <div id="tour-header" className="text-2xl mb-4 pl-4 flex items-center w-full">
        <h1 className="flex items-center">
          <TooltipTrigger classNames={'inline-flex'} tooltip={`Target Taxonomy: ${props.order.targetTaxonomy}`}>
            {getIconFromTaxa(props.order.targetTaxonomy, 'inline-block align-baseline mr-3')}
          </TooltipTrigger>
          <span className={'text-wrap break-all'}>{props.order.name}</span>
        </h1>
        <OrderAlertIndicator
          alerts={[
            ...noticeAlerts(notices),
            ...qualityFilterLossAlerts(poolItems),
            ...unassignedAlerts(otuProcessingData?.unassignedSamples)
          ]}
        />
        {orderToStateWidget(orderState, pipelineState)}
        {awaitsPostprocessing(orderState) && <IntermediatesExpiredBadge status={intermediates} />}
        <SpinPendingBadge description={spinStatus} className="ml-2" />
      </div>
      {props.order.description ? <p className="pl-4 -mt-2 mb-4 text-sm text-neutral-600 whitespace-pre-wrap">{props.order.description}</p> : null}
      {platformConflict && (
        <div role="alert" className="alert alert-error mb-4 flex items-start gap-2" data-testid="platform-processing-type-conflict">
          <span>{platformConflict}</span>
        </div>
      )}
      {awaitsPostprocessing(orderState) && <IntermediatesBanner status={intermediates} />}
      <hr />

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="divide-y grow p-4">
          <OrderDetailInfoWidget
            order={props.order}
            message$={message$}
            samplePools={props.samplePools}
            tokenChecksum={props.tokenChecksum}
            setInviteLabModalOpen={() => openModal('inviteLab', { orderId: props.order.id })}
            otuCount={props.otuCount}
            setClaimLabModalOpen={() =>
              openModal('claimAsLab', {
                orderId: props.order.id,
                tokenChecksum: props.tokenChecksum,
                targetTaxa: props.order.targetTaxonomy
              })
            }
          />
          <OrderDetailActionBar order={props.order} samplePools={props.samplePools} />
        </div>
        <div className="sm:max-md:w-full sm:max-md:flex sm:max-md:justify-center">
          <SamplesMap
            samples={props.samplePools === 'LOADING' ? [] : props.samplePools.flatMap((sp) => sp.samples)}
            metaData={metaData}
            style={{ display: isCompactMode ? 'none' : 'inherit', position: 'relative', width: '350px', height: '350px' }}
          />
        </div>
      </div>

      <OrderDetailTabSection order={props.order} samplePools={props.samplePools} orderState={orderState} message$={message$} />
      <Toast messageInterval={5000} message$={message$}></Toast>
    </div>
  )
}
